import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  CharacterSheetInterface,
  Condition,
} from "@dndModels";
import { DisablableOption } from "@models";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";
import ConditionBox from "@dndComponents/other/ConditionBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";

interface Props {
  char_int?: CharacterSheetInterface;
  active_conditions: { [id: string]: { level: number, source_true_id: string } };
  starred: string[];
  immune: string[];
  extra_ids?: string[];
  // exhaustion_id?: string;
  disabled: boolean;
  color?: string;
  onChange: (condition_id: string, level: number, max_level: number) => void;
  onStar: (id: string) => void;
  onImmune: (id: string) => void;
  onCharChange?: () => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function ConditionBar({ 
  char_int,
  active_conditions,
  starred,
  immune,
  extra_ids = [],
  disabled,
  onChange,
  onStar,
  onImmune,
  color = "lightgray",
  // exhaustion_id = "605cb11c1fca68233ca9e1cc",
  onCharChange = () => {}
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);

  useEffect(() => {
    if (!fetched) {
      load();
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const load = () => {
    setLoading(true);
    api.getSetOfObjects({ data_types: ["condition"]}).then(res => {
      setConditions(res.condition as Condition[]);
      setLoading(false);
    });
    setFetched(true);
  }

  const renderCondition = (cond: Condition) => {
    return (
      <ConditionBox key={cond._id}
        char_int={char_int}
        disabled={disabled}
        starred={starred.includes(cond._id)}
        immune={immune.includes(cond._id)}
        // active={active_conditions[cond._id]}
        condition={cond}
        color={color}
        onChange={(level: number) => {
          onChange(cond._id, level, cond.max_level);
        }}
        onStar={() => {
          onStar(cond._id);
          setReloading(true);
        }}
        onImmune={() => {
          onImmune(cond._id);
        }}
        onCharChange={onCharChange}
      />
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    // debugger;
    const keys = Object.keys(active_conditions);
    const active_conditions2 = conditions.filter(o => keys.includes(o._id));
    const alternate_condition_originals = char_int ? char_int.alternate_condition_features.map(o => o.original_condition_id) : [];
    const alternate_conditions = char_int ? char_int.alternate_condition_features.map(o => o.alternate_condition_id) : [];
    
    // Iterate through conditions
    const other_common_conditions = conditions.filter(o => 
      !keys.includes(o._id) && !starred.includes(o._id) && !alternate_condition_originals.includes(o._id) &&
      (o.common || alternate_conditions.includes(o._id) || common_util.anyOverlaps(o.class_ids, extra_ids) || common_util.anyOverlaps(o.subclass_ids, extra_ids))).map(o => {
      const opt: DisablableOption = {
        id: o._id, text: o.use_name
      };
      return opt;
    });
    const other_rare_conditions = conditions.filter(o => 
      !keys.includes(o._id) && !starred.includes(o._id) && !alternate_conditions.includes(o._id) &&
      !o.common && !common_util.anyOverlaps(o.class_ids, extra_ids) && !common_util.anyOverlaps(o.subclass_ids, extra_ids)).map(o => {
      const opt: DisablableOption = {
        id: o._id, text: o.use_name
      };
      return opt;
    });
    return (
      <>
        { !reloading && active_conditions2.filter(o => starred.includes(o._id)).map(cond => {
          return renderCondition(cond);
        })}
        { !reloading && active_conditions2.filter(o => !starred.includes(o._id)).map(cond => {
          return renderCondition(cond);
        })}
        { !reloading && conditions.filter(o => !keys.includes(o._id) && starred.includes(o._id)).map(cond => {
          return renderCondition(cond);
        })}
        <span style={{ borderRight: "1px solid", width: "200px" }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              { !reloading && 
                <SelectObjectBox
                  name="Other Common Conditions"
                  extra_options={other_common_conditions}
                  value=""
                  onChange={(changed: string) => {
                    onStar(changed);
                    setReloading(true);
                  }}
                />
              }
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              { !reloading && 
                <SelectObjectBox
                  name="Other Rare Conditions"
                  extra_options={other_rare_conditions}
                  value=""
                  onChange={(changed: string) => {
                    onStar(changed);
                    setReloading(true);
                  }}
                />
              }
            </Grid>
          </Grid>
        </span>
      </>
    );
  }
}
